import { type ThreadMessageLike, useExternalStoreRuntime } from '@assistant-ui/react';
import { useState } from 'react';
import type { MessageAttachment } from '../../generated/api/types.gen.js';
import type { UploadAttachments } from '../conversations/upload-attachments.js';
import type { ConversationCommandContext } from '../../hooks/useConversationCommands.js';
import type { ConversationStream } from '../../hooks/useConversationStream.js';
import {
  appendMessageText,
  isTurnRunning,
  liveTurnMessage,
  toThreadAttachment,
  toThreadMessages,
} from './conversation-runtime.js';

interface PendingMessage {
  id: string;
  content: string;
  sentAt: number;
  /** The files it carries, shown in the bubble until the server's copy replaces it. */
  files: MessageAttachment[];
}

interface ConversationRuntimeOptions {
  /** Slash-command selections of the detailed composer. */
  commandContext?: ConversationCommandContext | null;
  /** What the page shows right now (an entity assistant sends its form with every message). */
  contextSpec?: Record<string, unknown>;
  /** A send failed; the optimistic bubble is already gone. Tell the person (toast) — never fail silently. */
  onSendError?: (error: Error) => void;
  /**
   * The composer's attachments (`upload-attachments.ts`): with it the person can attach files, each uploaded as it is
   * picked, and a message carries their upload ids — files alone are a message.
   */
  uploads?: UploadAttachments;
}

/** A server copy of the person's message landed (same text and files, sent within the clock-skew window). */
const ECHO_WINDOW_MS = 5_000;

const uploadIds = (files: readonly MessageAttachment[] | undefined) =>
  (files ?? []).map((file) => file.uploadId).join();

/**
 * The assistant-ui runtime for one conversation, over the existing `useConversationStream` (pass the stream down;
 * never open a second one). The person's message shows at once as an optimistic bubble until the server's copy
 * arrives; the live turn (thinking, tools, streamed text) is one running message at the end.
 */
export function useConversationRuntime(stream: ConversationStream, options: ConversationRuntimeOptions = {}) {
  const [pending, setPending] = useState<PendingMessage[]>([]);

  const echoed = (p: PendingMessage) =>
    stream.messages.some(
      (m) =>
        m.role === 'user' &&
        m.content.trim() === p.content &&
        uploadIds(m.attachments) === uploadIds(p.files) &&
        Date.parse(m.createdAt) >= p.sentAt - ECHO_WINDOW_MS,
    );
  const waiting = pending.filter((p) => !echoed(p));
  const live = liveTurnMessage(stream.eventsState, stream.awaitingAssistant);

  const messages: ThreadMessageLike[] = [
    ...toThreadMessages(stream.messages),
    ...waiting.map(
      (p): ThreadMessageLike => ({
        id: p.id,
        role: 'user',
        content: p.content,
        createdAt: new Date(p.sentAt),
        ...(p.files.length > 0 ? { attachments: p.files.map(toThreadAttachment) } : {}),
      }),
    ),
    ...(live ? [live] : []),
  ];

  return useExternalStoreRuntime<ThreadMessageLike>({
    messages,
    convertMessage: (message) => message,
    isRunning: stream.isSending || isTurnRunning(stream.eventsState, stream.awaitingAssistant),
    isLoading: stream.isLoading,
    ...(options.uploads ? { adapters: { attachments: options.uploads } } : {}),
    onNew: async (message) => {
      const text = appendMessageText(message);
      // The adapter's send waited for every file; one that is not ready stops the message, never drops from it.
      const files: MessageAttachment[] = [];
      for (const attachment of message.attachments ?? []) {
        const file = options.uploads?.attachmentOf(attachment.id);
        if (!file) {
          options.onSendError?.(new Error(`"${attachment.name}" did not upload; attach it again.`));
          return;
        }
        files.push(file);
      }
      if (!text && files.length === 0) return;
      const sentAt = Date.now();
      const id = `pending-${sentAt}`;
      setPending((current) => [...current.filter((p) => !echoed(p)), { id, content: text, sentAt, files }]);
      await new Promise<void>((resolve) => {
        stream.send(text, {
          commandContext: options.commandContext ?? null,
          ...(options.contextSpec ? { contextSpec: options.contextSpec } : {}),
          ...(files.length > 0 ? { attachments: files.map((file) => file.uploadId) } : {}),
          onSuccess: () => resolve(),
          onError: (error) => {
            setPending((current) => current.filter((p) => p.id !== id));
            options.onSendError?.(error);
            resolve();
          },
        });
      });
    },
    onLoadEarlier: stream.hasOlder
      ? async () => {
          await stream.fetchOlder();
        }
      : undefined,
  });
}
